import { useState } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahgtn3oh653ivfvnuv0";

type Status = "idle" | "submitting" | "success" | "error";

export default function LeadForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("线索已收到，我们会先做交叉验证再决定是否采用。");
      form.reset();
      return;
    }

    payload.delete("website_alt");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam") || serverMsg.includes("垃圾");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("线索已收到，我们会先做交叉验证再决定是否采用。感谢你提供可核查的物料。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试，或直接发送邮件给我们。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功。请稍后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <form
      id="lead-form-1"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-xl font-bold text-foreground-950">订阅更新与投递线索</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-700">
        留下邮箱即可收到新一期赛果解析。如果你手上有公示照片、通稿截图、赛程文件或票数变化记录，也欢迎一并说明。
      </p>

      <div className="mt-5 flex flex-col gap-4">
        <div>
          <label htmlFor="lead-email" className="mb-2 block font-label text-sm text-foreground-800">
            邮箱
          </label>
          <input
            id="lead-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="lead-type" className="mb-2 block font-label text-sm text-foreground-800">
            线索类型
          </label>
          <select
            id="lead-type"
            name="线索类型"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          >
            <option value="仅订阅更新">仅订阅更新</option>
            <option value="评分口径异常">评分口径异常</option>
            <option value="评委与赞助关系">评委与赞助关系</option>
            <option value="计分流程留痕">计分流程留痕</option>
            <option value="网络票数异常">网络票数异常</option>
            <option value="合约与声明时间线">合约与声明时间线</option>
            <option value="内容纠错">内容纠错</option>
          </select>
        </div>

        <div>
          <label htmlFor="lead-detail" className="mb-2 block font-label text-sm text-foreground-800">
            线索说明（选填，500 字以内）
          </label>
          <textarea
            id="lead-detail"
            name="线索说明"
            rows={4}
            maxLength={500}
            placeholder="请尽量说明物料来源、时间与可核查的公开记录。"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          ></textarea>
        </div>

        <div className="form-guard-field" aria-hidden="true">
          <label htmlFor="lead-website-alt">备用联系字段</label>
          <input
            id="lead-website-alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>

        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
        >
          <i className="ri-send-plane-line" aria-hidden="true"></i>
          {status === "submitting" ? "提交中..." : "提交"}
        </button>

        {message && (
          <p
            role="status"
            className={[
              "rounded-md px-4 py-3 text-sm leading-relaxed",
              status === "success"
                ? "bg-accent-100 text-accent-900"
                : "bg-primary-100 text-primary-800",
            ].join(" ")}
          >
            {message}
          </p>
        )}
      </div>
    </form>
  );
}